import React, { useEffect, useState } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import styles from './TextQuestion.module.scss';
import {
  PersonalStatementQuestion,
  PersonalStatementAnswerExample,
  CommonHash,
  PersonalStatementAnswer,
} from 'app/types';
import Text from 'common/components/Text/Text';

interface CompProps {
  question: PersonalStatementQuestion;
  answers: CommonHash;
  setAnswer: (h: CommonHash) => void;
  optOut?: boolean;
}

interface ExampleProps {
  examples?: PersonalStatementAnswerExample[];
}
const ExampleCarousel = ({ examples }: ExampleProps): React.ReactElement => {
  const [index, setIndex] = useState(0);

  const handleSelect = (selectedIndex: number) => {
    setIndex(selectedIndex);
  };

  return (
    <Carousel activeIndex={index} onSelect={handleSelect} className={styles.questionCarousel}>
      {examples?.map((example) => (
        <Carousel.Item key={example.id}>
          <Text size={14} weight={400} inlineStyle={{ lineHeight: '21px', textAlign: 'justify' }}>
            {example.content}
          </Text>
        </Carousel.Item>
      ))}
    </Carousel>
  );
};

const TextQuestion = ({ question, setAnswer, answers, optOut }: CompProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const [text, setText] = useState(answers[question.key]?.content?.text || '');

  const [wordCount, setWordCount] = useState(0);

  const [optOutChecked, setOptOutChecked] = useState<boolean>(
    answers[question.key]?.content?.text === 'I haven’t lived or studied abroad before.'
  );

  const setInputOption = (key: string, value: any) => {
    setAnswer((a: any) => ({ ...a, [key]: { ...a[key], ...value } }));
  };
  useEffect(() => {
    const words = text.split(' ');
    let wordCount = 0;
    words.forEach((word: string) => {
      if (word.trim() !== '') {
        wordCount++;
      }
    });
    if (question.max_words_amount && wordCount > question.max_words_amount) {
      setText((prevText: string) => prevText.slice(0, -1));
      setInputOption(question.key, { content: { text: text.slice(0, -1) } });
    } else {
      setInputOption(question.key, { content: { text: text } });
    }
    setWordCount(wordCount);
  }, [text]);

  const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
  };

  useEffect(() => {
    if (optOut) {
      if (optOutChecked) {
        setText('I haven’t lived or studied abroad before.');
      } else {
        if (answers[question.key]?.content?.text === 'I haven’t lived or studied abroad before.') {
          setText('');
        }
      }
    }
  }, [optOutChecked]);

  return (
    <div className={styles.questionContainer}>
      <div className={styles.questionHeader}>
        <Text size={18} weight={600}>
          {question?.title}
        </Text>
        <Text size={14} weight={400} inlineStyle={{ color: '#727D9B' }}>
          {question?.subtitle}
        </Text>
      </div>
      {optOut && (
        <div className="d-flex flex-row align-items-center gap-2">
          <input
            type="checkbox"
            name="opt_out"
            value="I haven't lived or studied abroad"
            className="form-check-input"
            checked={optOutChecked}
            onChange={() => setOptOutChecked(!optOutChecked)}
            style={{ marginTop: '0' }}
          />
          <Text size={14} weight={600} inlineStyle={{ color: '#19234F' }}>
            I haven&apos;t lived or studied abroad before
          </Text>
        </div>
      )}
      <div className={styles.questionBody}>
        <textarea
          className={styles.textArea}
          placeholder="Type your answer"
          value={text !== 'I haven’t lived or studied abroad before.' ? text : ''}
          onChange={changeHandler}
          disabled={optOutChecked}
        ></textarea>
        <div className={styles.wordCount}>
          <Text size={14} weight={600}>
            {text !== 'I haven’t lived or studied abroad before.'
              ? `${wordCount}/${question?.max_words_amount}`
              : `0/${question?.max_words_amount}`}
          </Text>
        </div>
      </div>
      <div className={styles.questionExample}>
        <div className={styles.header}>
          <Text size={14} weight={600}>
            Example
          </Text>
          <Button
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className={styles.collapseButton}
            aria-controls="hola"
            type="button"
          >
            <div>
              <i className={open ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}></i>
            </div>
          </Button>
        </div>
        <div className={styles.body}>
          <Collapse in={open}>
            <div id="hola" className={styles.collapseBody}>
              <ExampleCarousel examples={question?.answer_examples} />
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};

export default TextQuestion;
